import { Box, Flex, Text, Textarea, TextareaProps, useMergeRefs } from '@chakra-ui/react';
import { rem } from 'polished';
import { ChangeEvent, forwardRef, useEffect, useRef, useState } from 'react';
import { colors } from '../../theme/colors';
import { paddedScrollStyles } from '../ScrollContainer/ScrollContainer';
import { TextareaAutogrow } from '../TextareaAutogrow';

export const TextareaCounter = forwardRef<
  HTMLTextAreaElement,
  TextareaProps & { spaceAfter?: string; autogrow?: boolean }
>(({ maxLength, onChange, spaceAfter, autogrow, ...props }, ref) => {
  const TextareaComponent = autogrow ? TextareaAutogrow : Textarea;
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const refs = useMergeRefs(inputRef, ref);
  const [characterAmount, setCharacterAmount] = useState(0);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onChange?.(e);
    setCharacterAmount(e.target.value.length);
  };

  useEffect(() => {
    if (!inputRef.current) return;
    setCharacterAmount(inputRef.current.value.length);
  }, [inputRef, props.value]);

  return (
    <Box mb={spaceAfter} pos="relative">
      <TextareaComponent
        maxLength={maxLength}
        onChange={handleChange}
        pb="space.32"
        ref={refs}
        {...(autogrow && { minH: props.rows ? rem(props.rows * 28 + 48) : rem(120 + 48) })}
        {...props}
        css={{
          ...paddedScrollStyles,
          '&::-webkit-resizer': {
            background: props.bg ? String(props.bg) : colors.blackBg,
            borderWidth: '8px',
            borderStyle: 'solid',
            borderColor: `transparent ${colors.gray[4]} ${colors.gray[4]} transparent`,
          },
        }}
      />
      {!!maxLength && (
        <Box
          pointerEvents="none"
          w="100%"
          pos="absolute"
          bottom={0}
          px="space.24"
          zIndex={2}
          borderBottom="1px solid transparent"
        >
          <Flex
            borderTop={autogrow ? 'none' : '1px solid'}
            borderTopColor="gray.4"
            py="space.4"
            justify="flex-end"
            w="100%"
            bg={autogrow ? 'unset' : props.bg || 'blackBg'}
          >
            <Text pointerEvents="none" zIndex={1} fontSize="md" color="gray.1">
              {characterAmount}
              <Text
                as="span"
                color={characterAmount >= maxLength ? 'gray.1' : 'gray.5'}
                display="inline"
              >
                {' / '}
                {maxLength}
              </Text>
            </Text>
          </Flex>
        </Box>
      )}
    </Box>
  );
});
